import { useState } from "react";
import { useCfops, useCstPisCofinsCombo } from "@/features/fiscal/hooks/useFiscal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { type TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import type { CfopDto, SalvarCfopRequest } from "@/features/fiscal/types";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Search, FileCode, Plus, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { AvisoTabelaNacional } from "@/components/AvisoTabelaNacional";

// ── Formulário vazio ───────────────────────────────────────────────────────
const EMPTY: SalvarCfopRequest = {
  codigo:         "",
  descricao:      "",
  calculaIcms:    false,
  dtVigencia:     null,
  grupoCfopId:    0,
  subgrupoCfopId: 0,
  cstAceito:      "",
  cstPis:         "",
  cstCofins:      "",
  informacaoFisco: "",
};

export default function Cfop() {
  const [busca, setBusca]           = useState("");
  const [pagina, setPagina]         = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId]     = useState<number | null>(null);
  const [editId, setEditId]         = useState<number | null>(null);
  const [form, setForm]             = useState<SalvarCfopRequest>(EMPTY);
  const [saving, setSaving]         = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting]   = useState(false);
  const [importResult, setImportResult] = useState<{ inseridos: number; atualizados: number; erros: number } | null>(null);

  const { user } = useAuth();
  const podeEditar = user?.isAdminGlobal ?? false;

  const { query, obterPorId, criar, atualizar, excluir, importar } = useCfops(busca || undefined, pagina, tamanhoPagina);
  const cfops = query.data?.itens ?? [];

  // CST PIS/COFINS cadastrados (para os selects do formulário)
  const { data: cstPisCofinsList = [] } = useCstPisCofinsCombo();
  const cstPisCofinsOpcoes = cstPisCofinsList.filter(i => i.cst);

  const handleImport = async () => {
    if (!importFile) { toast.error("Selecione o arquivo CSV."); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const r = await importar.mutateAsync(importFile);
      setImportResult(r);
      toast.success(`Importação concluída: ${r.inseridos} novos, ${r.atualizados} atualizados.`);
    } catch {
      toast.error("Erro ao importar. Verifique o cabeçalho do CSV (coluna CFOP obrigatória).");
    } finally {
      setImporting(false);
    }
  };

  const totalItens = query.data?.total ?? 0;
  const primeiroItem = totalItens === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(pagina * tamanhoPagina, totalItens);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const set = (field: keyof SalvarCfopRequest, value: unknown) =>
    setForm(f => ({ ...f, [field]: value }));

  const openNew = () => {
    setEditId(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = async (id: number) => {
    try {
      const dto: CfopDto = await obterPorId(id);
      setEditId(id);
      setForm({
        codigo:         dto.codigo ?? "",
        descricao:      dto.descricao ?? "",
        calculaIcms:    dto.calculaIcms,
        dtVigencia:     dto.dtVigencia ? dto.dtVigencia.substring(0, 10) : null,
        grupoCfopId:    dto.grupoCfopId,
        subgrupoCfopId: dto.subgrupoCfopId,
        cstAceito:      dto.cstAceito ?? "",
        cstPis:         dto.cstPis ?? "",
        cstCofins:      dto.cstCofins ?? "",
        informacaoFisco: dto.informacaoFisco ?? "",
      });
      setDialogOpen(true);
    } catch {
      toast.error("Erro ao carregar CFOP.");
    }
  };

  const handleSave = async () => {
    if (!form.codigo?.trim()) {
      toast.error("Código é obrigatório.");
      return;
    }
    if (!form.descricao?.trim()) {
      toast.error("Descrição é obrigatória.");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await atualizar.mutateAsync({ id: editId, req: form });
        toast.success("CFOP atualizado.");
      } else {
        await criar.mutateAsync(form);
        toast.success("CFOP criado.");
      }
      setDialogOpen(false);
    } catch {
      toast.error("Erro ao salvar CFOP.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      await excluir.mutateAsync(deleteId);
      toast.success("CFOP excluído.");
    } catch {
      toast.error("Erro ao excluir CFOP.");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileCode className="h-6 w-6" /> CFOP
          </h1>
          <p className="text-muted-foreground">Código Fiscal de Operações e Prestações</p>
        </div>
        {podeEditar && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setImportResult(null); setImportFile(null); setImportOpen(true); }}>
              <Upload className="h-4 w-4 mr-2" /> Importar CSV
            </Button>
            <Button onClick={openNew}>
              <Plus className="h-4 w-4 mr-2" /> Novo CFOP
            </Button>
          </div>
        )}
      </div>

      {!podeEditar && <AvisoTabelaNacional />}

      {/* Busca */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por código ou descrição..."
          className="pl-9"
          value={busca}
          onChange={e => { setBusca(e.target.value); setPagina(1); }}
        />
      </div>

      {/* Tabela */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Código</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="w-[110px]">Vigência</TableHead>
              <TableHead className="w-[80px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : cfops.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  Nenhum CFOP encontrado.
                </TableCell>
              </TableRow>
            ) : cfops.map(c => (
              <TableRow key={c.id}>
                <TableCell className="font-mono font-semibold">{c.codigo ?? "—"}</TableCell>
                <TableCell className="text-sm">{c.descricao ?? "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {c.dtVigencia ? new Date(c.dtVigencia).toLocaleDateString("pt-BR") : "—"}
                </TableCell>
                <TableCell>
                  {podeEditar && (
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(c.id)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(c.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {query.data && (
        <PaginacaoRodape
          pagina={pagina}
          totalPaginas={query.data.totalPaginas}
          totalItens={totalItens}
          primeiroItem={primeiroItem}
          ultimoItem={ultimoItem}
          tamanhoPagina={tamanhoPagina}
          onPaginaChange={setPagina}
          onTamanhoPaginaChange={setTamanhoPagina}
        />
      )}

      {/* Dialog Criar / Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editId ? "Editar CFOP" : "Novo CFOP"}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-2">
            {/* Código */}
            <div className="space-y-1">
              <Label>Código <span className="text-destructive">*</span></Label>
              <Input
                value={form.codigo ?? ""}
                onChange={e => set("codigo", e.target.value)}
                maxLength={10}
                placeholder="Ex.: 5101"
              />
            </div>

            {/* Vigência */}
            <div className="space-y-1">
              <Label>Vigência</Label>
              <Input
                type="date"
                value={form.dtVigencia ?? ""}
                onChange={e => set("dtVigencia", e.target.value || null)}
              />
            </div>

            {/* Descrição */}
            <div className="col-span-2 space-y-1">
              <Label>Descrição <span className="text-destructive">*</span></Label>
              <Input
                value={form.descricao ?? ""}
                onChange={e => set("descricao", e.target.value)}
                maxLength={350}
              />
            </div>

            {/* Grupo CFOP */}
            <div className="col-span-2 space-y-1">
              <Label>Grupo CFOP</Label>
              <Select
                value={form.grupoCfopId ? String(form.grupoCfopId) : ""}
                onValueChange={v => set("grupoCfopId", Number(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">01 - ENTRADAS OU AQUISIÇÕES DE SERVIÇOS</SelectItem>
                  <SelectItem value="2">02 - SAÍDAS OU PRESTAÇÕES DE SERVIÇOS</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* CST PIS / COFINS — seleção dos CST cadastrados */}
            <div className="space-y-1">
              <Label>CST PIS</Label>
              <Select value={form.cstPis || ""} onValueChange={v => set("cstPis", v)}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {cstPisCofinsOpcoes.map(item => (
                    <SelectItem key={`pis-${item.id}`} value={item.cst!}>{item.cst} — {item.descricao}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>CST COFINS</Label>
              <Select value={form.cstCofins || ""} onValueChange={v => set("cstCofins", v)}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {cstPisCofinsOpcoes.map(item => (
                    <SelectItem key={`cofins-${item.id}`} value={item.cst!}>{item.cst} — {item.descricao}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Informação ao Fisco */}
            <div className="col-span-2 space-y-1">
              <Label>Informação ao Fisco</Label>
              <Textarea
                value={form.informacaoFisco ?? ""}
                onChange={e => set("informacaoFisco", e.target.value)}
                maxLength={500}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Excluir */}
      <AlertDialog open={deleteId !== null} onOpenChange={open => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir CFOP?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Importar CSV */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Importar CFOP (CSV)</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p className="text-muted-foreground">
              CSV com cabeçalho e separador <code className="text-xs">;</code>. Coluna obrigatória:{" "}
              <code className="text-xs">CFOP</code>. Opcionais: <code className="text-xs">Descricao</code>,{" "}
              <code className="text-xs">Vigencia</code> e indicadores
              (<code className="text-xs">indNFe, indComunica, indTransp, indDevol, indRetor, indAnula, indRemes, indComb</code>).
              Atualiza por código — só altera as colunas presentes no arquivo.
            </p>
            <Input
              type="file"
              accept=".csv,text/csv"
              onChange={e => { setImportFile(e.target.files?.[0] ?? null); setImportResult(null); }}
            />
            {importResult && (
              <div className="rounded-md border bg-muted/40 p-3 space-y-1">
                <div>✅ Inseridos: <b>{importResult.inseridos}</b></div>
                <div>♻️ Atualizados: <b>{importResult.atualizados}</b></div>
                {importResult.erros > 0 && <div className="text-destructive">⚠️ Erros: <b>{importResult.erros}</b></div>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Fechar</Button>
            <Button onClick={handleImport} disabled={importing || !importFile}>
              {importing ? "Importando..." : "Importar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
